import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config';
import { useAuth } from '../contexts/AuthContext';
import Swal from 'sweetalert2';
import { PenTool, CheckCircle, Home } from 'lucide-react';

export default function ConfirmarFirma() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const [socio, setSocio] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSocio = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/socio/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Error al cargar datos del socio');
                const data = await response.json();
                setSocio(data);
            } catch (error) {
                console.error(error);
                Swal.fire('Error', 'No se pudieron cargar los datos del socio', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchSocio();
    }, [id, token]);

    const handleConfirmarFirma = async () => {
        try {
            const result = await Swal.fire({
                title: '¿Confirmar Firma del Socio?',
                text: "Al confirmar, se marcará el contrato como firmado y la solicitud pasará a estado PENDIENTE de aprobación por gerencia.",
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3B82F6',
                confirmButtonText: 'Sí, Confirmar Firma',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await fetch(`${API_BASE_URL}/socio/${id}/firmar`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Error al registrar la firma');

                await Swal.fire({
                    title: '¡Firma Registrada!',
                    text: 'El proceso de admisión ha completado su primera etapa. El socio ahora espera aprobación final.',
                    icon: 'success',
                    confirmButtonText: 'Volver al Inicio'
                });

                navigate('/'); // O a donde prefiera el usuario, dashboard o listado
            }
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al registrar la firma', 'error');
        }
    };

    if (loading) return <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div></div>;
    if (!socio) return <div className="text-center py-12">No se encontró el socio.</div>;

    const isFisica = !!socio.personaFisica;
    const nombre = isFisica
        ? `${socio.personaFisica.nombres} ${socio.personaFisica.apellidos}`
        : socio.personaJuridica.razonSocial;
    const doc = isFisica
        ? socio.personaFisica.numeroDocumento
        : socio.personaJuridica.ruc;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <PenTool className="w-8 h-8 text-white" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Confirmación de Firma
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Paso final para completar la solicitud de admisión
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-200">
                    <div className="mb-6 pb-6 border-b border-gray-100">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Resumen del Socio</h3>
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <dt className="text-sm font-medium text-gray-500">Nombre Completo</dt>
                                <dd className="mt-1 text-sm text-gray-900 font-bold">{nombre}</dd>
                            </div>
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">Documento ID</dt>
                                <dd className="mt-1 text-sm text-gray-900">{doc}</dd>
                            </div>
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">Nro. Solicitud</dt>
                                <dd className="mt-1 text-sm text-gray-900">#{socio.id}</dd>
                            </div>
                        </dl>
                    </div>

                    <div className="space-y-4">
                        <button
                            onClick={handleConfirmarFirma}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Confirmar Firma del Documento
                        </button>

                        <button
                            onClick={() => navigate('/')}
                            className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            <Home className="w-5 h-5 mr-2" />
                            Volver al Inicio (Sin Confirmar)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
